/**
 * districtCode별 GeoJSON 영구 캐시 (IndexedDB)
 * - localStorage(5MB) 대비 IndexedDB(~50MB+) 사용으로 전체 구역 데이터 저장 가능
 * - 저장 위치: 브라우저 IndexedDB (개발자 도구 → Application → IndexedDB → moving-visit-admin-geo-json)
 * - 용량 초과 시 LRU 방식으로 오래된 항목 제거
 */

const DB_NAME = 'moving-visit-admin-geo-json';
const DB_VERSION = 1;
const STORE_GEOMETRIES = 'geometries';
const STORE_META = 'meta';
const META_KEY = 'cache-meta';
const MAX_CACHE_BYTES = 50 * 1024 * 1024; // 50MB (467구역 × ~50KB ≈ 23MB 수용)

interface CacheEntry {
  code: string;
  size: number;
  lastAccess: number;
}

interface CacheMeta {
  entries: CacheEntry[];
  totalSize: number;
}

function openDB(): Promise<IDBDatabase> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('window undefined'));
  }
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_GEOMETRIES)) {
        db.createObjectStore(STORE_GEOMETRIES, { keyPath: 'code' });
      }
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META);
      }
    };
  });
}

async function loadMeta(db: IDBDatabase): Promise<CacheMeta> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_META, 'readonly');
    const req = tx.objectStore(STORE_META).get(META_KEY);
    req.onsuccess = () => {
      const parsed = req.result as CacheMeta | undefined;
      resolve(
        parsed && Array.isArray(parsed.entries)
          ? {
              entries: parsed.entries,
              totalSize: typeof parsed.totalSize === 'number' ? parsed.totalSize : 0,
            }
          : { entries: [], totalSize: 0 }
      );
    };
    req.onerror = () => reject(req.error);
  });
}

async function saveMeta(db: IDBDatabase, meta: CacheMeta): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_META, 'readwrite');
    const req = tx.objectStore(STORE_META).put(meta, META_KEY);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async function evictUntilUnderLimit(db: IDBDatabase, meta: CacheMeta): Promise<CacheMeta> {
  if (meta.totalSize <= MAX_CACHE_BYTES) return meta;

  const sorted = [...meta.entries].sort((a, b) => a.lastAccess - b.lastAccess);
  let totalSize = meta.totalSize;
  const evictedCodes = new Set<string>();

  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_GEOMETRIES, 'readwrite');
    const store = tx.objectStore(STORE_GEOMETRIES);
    for (const entry of sorted) {
      if (totalSize <= MAX_CACHE_BYTES) break;
      store.delete(entry.code);
      totalSize -= entry.size;
      evictedCodes.add(entry.code);
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });

  return {
    entries: meta.entries.filter((e) => !evictedCodes.has(e.code)),
    totalSize,
  };
}

/**
 * districtCode에 해당하는 GeoJSON geometry 문자열 조회 (캐시 우선)
 */
export async function getGeoJsonFromCache(districtCode: string): Promise<string | undefined> {
  if (typeof window === 'undefined') return undefined;

  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_GEOMETRIES, 'readwrite');
      const req = tx.objectStore(STORE_GEOMETRIES).get(districtCode);
      req.onsuccess = async () => {
        const row = req.result as { code: string; geometry: string } | undefined;
        if (!row?.geometry) {
          db.close();
          resolve(undefined);
          return;
        }
        const meta = await loadMeta(db);
        const entry = meta.entries.find((e) => e.code === districtCode);
        if (entry) {
          entry.lastAccess = Date.now();
          await saveMeta(db, meta);
        }
        db.close();
        resolve(row.geometry);
      };
      req.onerror = () => {
        db.close();
        reject(req.error);
      };
    });
  } catch {
    return undefined;
  }
}

/**
 * districtCode별 geometry 문자열을 캐시에 저장
 */
export async function setGeoJsonToCache(districtCode: string, geometryStr: string): Promise<void> {
  await setGeoJsonBulkToCache({ [districtCode]: geometryStr });
}

/**
 * 여러 districtCode의 geometry를 한 번에 캐시에 저장
 */
export async function setGeoJsonBulkToCache(geometries: Record<string, string>): Promise<void> {
  if (typeof window === 'undefined' || Object.keys(geometries).length === 0) return;

  try {
    const db = await openDB();
    const now = Date.now();
    const meta = await loadMeta(db);

    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_GEOMETRIES, 'readwrite');
      const store = tx.objectStore(STORE_GEOMETRIES);
      for (const [code, geometryStr] of Object.entries(geometries)) {
        if (!geometryStr) continue;
        const size = new Blob([geometryStr]).size;

        const existing = meta.entries.find((e) => e.code === code);
        if (existing) {
          meta.totalSize -= existing.size;
          meta.entries = meta.entries.filter((e) => e.code !== code);
        }

        store.put({ code, geometry: geometryStr });
        meta.entries.push({ code, size, lastAccess: now });
        meta.totalSize += size;
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });

    const evicted = await evictUntilUnderLimit(db, meta);
    await saveMeta(db, evicted);
    db.close();
  } catch {
    /* ignore */
  }
}

/**
 * 여러 districtCode의 캐시 조회
 * @returns [캐시된 것 Map, 캐시 미스된 코드 배열]
 */
export async function getGeoJsonBulkFromCache(
  districtCodes: string[]
): Promise<[Map<string, string>, string[]]> {
  const cached = new Map<string, string>();
  const missed: string[] = [];

  for (const code of districtCodes) {
    const val = await getGeoJsonFromCache(code);
    if (val) cached.set(code, val);
    else missed.push(code);
  }
  return [cached, missed];
}
